"use client";

import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import Footer from "./Footer";
import ERPLeadBot from "./ERPLeadBot";
import {
  Package,
  CheckCircle2,
  BarChart3,
  Bell,
  ShieldCheck,
  Smartphone,
  AlertTriangle,
  Users,
  ShoppingCart,
  Warehouse,
  Boxes,
  TrendingUp,
  ClipboardList,
} from "lucide-react";

const features = [
  {
    icon: Boxes,
    title: "Real-Time Stock Tracking",
    description:
      "Monitor stock levels live across all locations, no more manual counting or spreadsheets.",
  },
  {
    icon: AlertTriangle,
    title: "Low-Stock Alerts",
    description:
      "Get automatic SMS and email alerts when any item falls below your set minimum quantity.",
  },
  {
    icon: Warehouse,
    title: "Multi-Location Inventory",
    description:
      "Manage stock across multiple warehouses or branches from a single central dashboard.",
  },
  {
    icon: ShoppingCart,
    title: "Purchase Orders",
    description:
      "Create, track, and manage purchase orders from request to delivery with approval workflows.",
  },
  {
    icon: Users,
    title: "Supplier Management",
    description:
      "Maintain supplier profiles, payment terms, and complete order history in one place.",
  },
  {
    icon: TrendingUp,
    title: "Demand Forecasting",
    description:
      "Predict future stock needs based on historical usage patterns and seasonal trends.",
  },
  {
    icon: BarChart3,
    title: "Inventory Reports",
    description:
      "Insights on stock movement, turnover rate, wastage, and valuation at a glance.",
  },
  {
    icon: ClipboardList,
    title: "Audit & Reconciliation",
    description:
      "Run physical stock audits and reconcile against system records with variance reports.",
  },
  {
    icon: ShieldCheck,
    title: "Role-Based Access",
    description:
      "Control who can view, edit, or approve stock transactions with granular permissions.",
  },
];

const benefits = [
  "Eliminate stock-outs with automated low-stock alerts",
  "Reduce excess stock and cut storage costs",
  "Track purchases and suppliers in one place",
  "Multi-location support from day one",
  "Cloud-based, accessible from any device",
  "Affordable pricing built for Indian businesses",
];

export default function InventoryManagement() {
  return (
    <div className="w-full">
      {/* Hero */}
      <section className="bg-gradient-to-br from-purple-700 via-purple-600 to-purple-500 py-20 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-white/15 border border-white/20 text-white text-sm font-medium px-4 py-1.5 rounded-full mb-6">
              <Package className="w-4 h-4" /> Inventory Management System
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
              Take Full Control of Your{" "}
              <span className="text-purple-200">Stock & Inventory</span>
            </h1>
            <p className="text-xl text-purple-100 mb-8 max-w-2xl mx-auto">
              Track items, manage suppliers, get low-stock alerts before you run
              out, all in one easy-to-use platform built for Indian businesses.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact?product=inventory">
                <Button size="lg" className="bg-white text-purple-700 hover:bg-purple-50">
                  Start Free Trial
                </Button>
              </Link>
              <Link to="/schedule-demo">
                <Button
                  size="lg"
                  variant="outline"
                  className="border-white bg-transparent text-white hover:bg-white/10 hover:text-white"
                >
                  Book a Demo
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Everything You Need to Manage Stock
            </h2>
            <p className="text-xl text-gray-500 max-w-xl mx-auto">
              From real-time tracking to demand forecasting, handle every aspect
              of your inventory operations.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, idx) => (
              <Card
                key={idx}
                className="border hover:border-purple-200 transition-colors"
              >
                <CardContent className="p-6">
                  <div className="w-11 h-11 bg-purple-50 rounded-lg flex items-center justify-center mb-4">
                    <feature.icon className="w-5 h-5 text-purple-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-gray-500 text-sm leading-relaxed">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Why Businesses Choose Clynox Inventory
              </h2>
              <p className="text-lg text-gray-500 mb-8">
                Businesses across India use Clynox to eliminate stock-outs, reduce
                manual errors, and get full visibility over their inventory.
              </p>
              <div className="space-y-4">
                {benefits.map((benefit, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 text-sm">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-6">
              {[
                { value: "100%", label: "Real-Time Accuracy" },
                { value: "0", label: "Manual Counting Required" },
                { value: "Multi", label: "Location Support" },
                { value: "24/7", label: "Stock Visibility" },
              ].map((stat, idx) => (
                <div
                  key={idx}
                  className="bg-white rounded-2xl p-6 text-center shadow-sm border"
                >
                  <div className="text-3xl font-bold text-purple-600 mb-1">
                    {stat.value}
                  </div>
                  <div className="text-sm text-gray-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-purple-600 to-purple-800">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Take Control of Your Inventory?
          </h2>
          <p className="text-xl text-purple-100 mb-8">
            Get started with a free 30-day trial. No credit card required.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact?product=inventory">
              <Button size="lg" className="bg-white text-purple-600 hover:bg-gray-100">
                Start Free Trial
              </Button>
            </Link>
            <Link to="/contact?product=inventory">
              <Button
                size="lg"
                variant="outline"
                className="border-white bg-transparent text-white hover:bg-white/10 hover:text-white"
              >
                Contact Sales
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      <ERPLeadBot />
    </div>
  );
}
